import { Trash2, ArrowRight, FileText } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export const Cart = ({ cart, onUpdateQuantity, onRemoveItem }) => {
  const navigate = useNavigate();
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h1 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight mb-8" data-testid="cart-title">
            Votre Panier
          </h1>
          <div className="bg-white rounded-xl p-12 text-center" data-testid="empty-cart">
            <p className="text-muted-foreground mb-6">Votre panier est vide</p>
            <Link
              to="/produits"
              className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-medium transition-all hover:scale-105 active:scale-95 shadow-lg shadow-primary/20 inline-flex items-center justify-center"
              data-testid="continue-shopping-btn"
            >
              Continuer mes achats
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <h1 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight mb-8" data-testid="cart-title">
          Votre Panier
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-xl p-6 flex gap-6"
                data-testid={`cart-item-${item.id}`}
              >
                <img
                  src={item.image_url}
                  alt={item.name}
                  className="w-24 h-24 object-cover rounded-lg"
                  data-testid={`cart-item-image-${item.id}`}
                />
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-lg mb-2" data-testid={`cart-item-name-${item.id}`}>
                    {item.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">{item.category}</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                      className="w-8 h-8 rounded-full border border-input hover:bg-muted transition-colors text-sm"
                      data-testid={`decrease-qty-${item.id}`}
                    >
                      -
                    </button>
                    <span className="w-8 text-center font-medium" data-testid={`cart-quantity-${item.id}`}>
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                      className="w-8 h-8 rounded-full border border-input hover:bg-muted transition-colors text-sm"
                      data-testid={`increase-qty-${item.id}`}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary mb-3" data-testid={`cart-item-price-${item.id}`}>
                    {(item.price * item.quantity).toFixed(2)} DH
                  </p>
                  <button
                    onClick={() => onRemoveItem(item.id)}
                    className="text-destructive hover:text-destructive/80 transition-colors"
                    data-testid={`remove-item-${item.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 sticky top-24" data-testid="cart-summary">
              <h3 className="font-heading font-semibold text-xl mb-6">Récapitulatif</h3>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Sous-total</span>
                  <span className="font-medium">{total.toFixed(2)} DH</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Livraison</span>
                  <span className="font-medium">{total >= 500 ? 'Gratuite' : '50.00 DH'}</span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="text-2xl font-bold text-primary" data-testid="cart-total">
                    {(total >= 500 ? total : total + 50).toFixed(2)} DH
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <Link
                  to="/checkout"
                  className="w-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-8 rounded-full font-medium transition-all hover:scale-105 active:scale-95 shadow-lg shadow-accent/20 flex items-center justify-center gap-2"
                  data-testid="proceed-checkout-btn"
                >
                  Passer la commande
                  <ArrowRight className="w-5 h-5" />
                </Link>

                <button
                  onClick={() => navigate('/demander-devis', { state: { fromCart: true } })}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-medium transition-all hover:scale-105 active:scale-95 shadow-lg shadow-primary/20 flex items-center justify-center gap-2"
                  data-testid="request-quote-btn"
                >
                  <FileText className="w-5 h-5" />
                  Demander un devis
                </button>
              </div>

              <Link
                to="/produits"
                className="block text-center text-sm text-primary hover:underline mt-4"
                data-testid="continue-shopping-link"
              >
                Continuer mes achats
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};